/**
 * `convert/dedupe-lines` — line deduplicator.
 *
 * Walks input lines once and keeps a Map / Set keyed by the
 * comparison value (raw line, or trim+lower depending on opts).
 *
 * Modes
 *   keepFirst  — when duplicates are seen, keep the first occurrence
 *                (default). When false, keep the LAST occurrence —
 *                useful for "merge config files where the bottom
 *                wins".
 *   emitCount  — output `<count>\t<line>` rows instead of bare lines,
 *                like `uniq -c`. Count reflects how many times the
 *                comparison key appeared in the input.
 *
 * Comparison normalisation
 *   trim             — `line.trim()` before comparing AND before
 *                       writing to the output.
 *   caseInsensitive  — fold to lowercase for the comparison key.
 *                       The OUTPUT preserves the original casing of
 *                       whichever occurrence we kept (first or last).
 *
 * Output line endings are normalised to LF.
 */

export interface DedupeOpts {
  /** Comparison: lowercase the key before hashing. Output preserves original case. */
  caseInsensitive?: boolean;
  /** Trim each line before comparing AND before writing it out. */
  trim?: boolean;
  /** When false, keep the LAST occurrence instead of the first. Default true. */
  keepFirst?: boolean;
  /** When true, prepend `<count>\t` to each output line. */
  emitCount?: boolean;
}

export interface DedupeResult {
  result: string;
  /** Lines kept after dedupe. */
  uniqueCount: number;
  /** Lines dropped as duplicates. */
  removedCount: number;
}

export function dedupeLines(
  input: string,
  opts: DedupeOpts = {},
): DedupeResult {
  if (!input) return { result: '', uniqueCount: 0, removedCount: 0 };

  const keepFirst = opts.keepFirst !== false; // default true
  const raw = input.split(/\r?\n/);
  const lines = opts.trim ? raw.map((l) => l.trim()) : raw;

  // We track:
  //   counts: comparison key → occurrence count
  //   slots:  insertion order of the line we'll emit (first / last)
  //   slotByKey: comparison key → index into `slots` so we can
  //              overwrite when keepFirst === false (last wins).
  const counts = new Map<string, number>();
  const slots: Array<{ key: string; line: string }> = [];
  const slotByKey = new Map<string, number>();

  for (const line of lines) {
    const key = opts.caseInsensitive ? line.toLowerCase() : line;
    counts.set(key, (counts.get(key) ?? 0) + 1);

    const existing = slotByKey.get(key);
    if (existing === undefined) {
      slotByKey.set(key, slots.length);
      slots.push({ key, line });
    } else if (!keepFirst) {
      slots[existing] = { key, line };
    }
  }

  const out: string[] = opts.emitCount
    ? slots.map(({ key, line }) => `${counts.get(key) ?? 1}\t${line}`)
    : slots.map(({ line }) => line);

  return {
    result: out.join('\n'),
    uniqueCount: slots.length,
    removedCount: lines.length - slots.length,
  };
}
